import { useEffect, useRef, useState, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import videojs from 'video.js'
import { 
  MediaMetadata, 
  PlayerConfiguration, 
  MediaPlayerState, 
  MediaPlayerRef,
  MediaPlayerError 
} from '@/types/media'
import { 
  extractMediaMetadata, 
  getMediaType, 
  extractVideoMetadata 
} from '@/lib/media-utils'
import { 
  getCosmicWaveformConfig, 
  getCosmicVideoConfig, 
  applyCosmicTheme,
  applyCosmicWaveformGlow,
  removeCosmicWaveformGlow
} from '@/lib/player-themes'

interface UseMediaPlayerProps {
  file: File
  config?: Partial<PlayerConfiguration>
  onPlayStateChange?: (isPlaying: boolean) => void
  onTimeUpdate?: (currentTime: number, duration: number) => void
  onMetadataLoad?: (metadata: MediaMetadata) => void
  onError?: (error: MediaPlayerError) => void
}

interface UseMediaPlayerReturn {
  state: MediaPlayerState
  playerRef: MediaPlayerRef
  videoRef: React.RefObject<HTMLVideoElement>
  waveformRef: React.RefObject<HTMLDivElement>
  audioRef: React.RefObject<HTMLAudioElement>
}

export const useMediaPlayer = ({
  file,
  config = {},
  onPlayStateChange,
  onTimeUpdate,
  onMetadataLoad,
  onError
}: UseMediaPlayerProps): UseMediaPlayerReturn => {
  
  // Refs for DOM elements and player instances
  const videoRef = useRef<HTMLVideoElement>(null)
  const waveformRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const wavesurferRef = useRef<any>(null)
  const videojsRef = useRef<any>(null)
  const blobUrlRef = useRef<string | null>(null)

  // Default configuration
  const defaultConfig: PlayerConfiguration = {
    mode: 'inline',
    showWaveform: true,
    showVideoControls: true,
    autoplay: false,
    loop: false,
    volume: 0.7,
    theme: 'cosmic',
    enableFullscreen: true,
    enablePictureInPicture: true
  }

  const finalConfig = { ...defaultConfig, ...config }
  const mediaType = getMediaType(file)

  // Player state
  const [state, setState] = useState<MediaPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: finalConfig.volume || 0.7,
    isMuted: false,
    isLoading: true,
    error: null,
    metadata: null
  })

  // Error handler
  const handleError = useCallback((error: MediaPlayerError) => {
    setState(prev => ({ ...prev, error, isLoading: false }))
    onError?.(error)
  }, [onError])

  // Cleanup function
  const cleanup = useCallback(() => {
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.destroy()
      } catch (error) {
        console.warn('Error destroying WaveSurfer:', error)
      }
      wavesurferRef.current = null
    }

    if (videojsRef.current) {
      try {
        videojsRef.current.dispose()
      } catch (error) {
        console.warn('Error disposing Video.js:', error)
      }
      videojsRef.current = null
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    // Clear waveform container and remove glow effects
    if (waveformRef.current) {
      removeCosmicWaveformGlow(waveformRef.current)
      waveformRef.current.innerHTML = ''
    }
  }, [])

  // Initialize player
  useEffect(() => {
    let isCancelled = false
    
    // Prevent double initialization in React Strict Mode
    if (wavesurferRef.current || videojsRef.current) {
      return
    }
    
    const initializePlayer = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }))

        // Cleanup previous instances
        cleanup()

        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 50))

        // Check if component was unmounted during delay
        if (isCancelled) return

        // Validate file
        if (!file || file.size === 0) {
          handleError({
            code: 'INVALID_FILE',
            message: 'Invalid media file'
          })
          return
        }

        // Create blob URL
        const blobUrl = URL.createObjectURL(file)
        blobUrlRef.current = blobUrl

        // Extract metadata
        let metadata: MediaMetadata
        if (mediaType === 'video') {
          metadata = await extractVideoMetadata(file)
        } else {
          metadata = await extractMediaMetadata(file)
        }

        setState(prev => ({ ...prev, metadata }))
        onMetadataLoad?.(metadata)

        // Initialize based on media type
        if (mediaType === 'video') {
          await initializeVideoPlayer(blobUrl)
        } else if (mediaType === 'audio') {
          await initializeAudioPlayer(blobUrl)
        } else {
          console.error('Unsupported media type:', mediaType, 'for file:', file.type)
          handleError({
            code: 'UNSUPPORTED_MEDIA',
            message: `Unsupported media type: ${mediaType}`
          })
          return
        }

        setState(prev => ({ ...prev, isLoading: false }))

      } catch (error) {
        console.error('Failed to initialize player:', error)
        handleError({
          code: 'INITIALIZATION_ERROR',
          message: error instanceof Error ? error.message : 'Failed to initialize player'
        })
      }
    }

    const initializeVideoPlayer = async (blobUrl: string) => {
      if (!videoRef.current) {
        throw new Error('Video element not available')
      }

      // Set video source
      videoRef.current.src = blobUrl

      // Initialize Video.js
      const videojsConfig = getCosmicVideoConfig({
        controls: finalConfig.showVideoControls
      })

      const player = videojs(videoRef.current, videojsConfig)
      videojsRef.current = player

      // Apply cosmic theme
      applyCosmicTheme(player)

      // Set up video player event listeners
      player.on('loadedmetadata', () => {
        setState(prev => ({ 
          ...prev, 
          duration: player.duration() || 0 
        }))
      })

      player.on('timeupdate', () => {
        const currentTime = player.currentTime() || 0
        const duration = player.duration() || 0
        setState(prev => ({ ...prev, currentTime }))
        onTimeUpdate?.(currentTime, duration)
      })

      player.on('play', () => {
        setState(prev => ({ ...prev, isPlaying: true }))
        onPlayStateChange?.(true)
      })

      player.on('pause', () => {
        setState(prev => ({ ...prev, isPlaying: false }))
        onPlayStateChange?.(false)
      })

      player.on('volumechange', () => {
        setState(prev => ({ 
          ...prev, 
          volume: player.volume() || 0,
          isMuted: player.muted() || false
        }))
      })

      player.on('error', (error: any) => {
        handleError({
          code: 'VIDEO_ERROR',
          message: 'Video playback error',
          details: error
        })
      })

      // Initialize waveform if enabled
      if (finalConfig.showWaveform && waveformRef.current) {
        await initializeWaveform(videoRef.current)
      }
    }

    const initializeAudioPlayer = async (blobUrl: string) => {
      if (!audioRef.current) {
        throw new Error('Audio element not available')
      }

      // Set audio source
      audioRef.current.src = blobUrl

      // Set up audio event listeners
      audioRef.current.addEventListener('loadedmetadata', () => {
        setState(prev => ({ 
          ...prev, 
          duration: audioRef.current?.duration || 0 
        }))
      })

      audioRef.current.addEventListener('timeupdate', () => {
        const currentTime = audioRef.current?.currentTime || 0
        const duration = audioRef.current?.duration || 0
        setState(prev => ({ ...prev, currentTime }))
        onTimeUpdate?.(currentTime, duration)
      })

      audioRef.current.addEventListener('play', () => {
        setState(prev => ({ ...prev, isPlaying: true }))
        onPlayStateChange?.(true)
      })

      audioRef.current.addEventListener('pause', () => {
        setState(prev => ({ ...prev, isPlaying: false }))
        onPlayStateChange?.(false)
      })

      audioRef.current.addEventListener('volumechange', () => {
        setState(prev => ({ 
          ...prev, 
          volume: audioRef.current?.volume || 0,
          isMuted: audioRef.current?.muted || false
        }))
      })

      audioRef.current.addEventListener('error', () => {
        handleError({
          code: 'AUDIO_ERROR',
          message: 'Audio playback error'
        })
      })

      // Initialize waveform
      if (finalConfig.showWaveform && waveformRef.current) {
        await initializeWaveform(audioRef.current)
      }
    }

    const initializeWaveform = async (mediaElement: HTMLMediaElement) => {
      if (!waveformRef.current) {
        console.warn('Waveform container not available, skipping waveform initialization')
        return
      }

      // Clear container
      waveformRef.current.innerHTML = ''

      const waveformConfig = getCosmicWaveformConfig()

      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: waveformConfig.waveColor,
        progressColor: waveformConfig.progressColor,
        cursorColor: waveformConfig.cursorColor,
        height: waveformConfig.height,
        normalize: waveformConfig.normalize,
        fillParent: waveformConfig.fillParent,
        responsive: waveformConfig.responsive
      })

      // Use the existing audio element approach instead of direct blob URL
      // This avoids the blob URL fetch issues in some environments
      ;(wavesurfer as any).loadBlob(file)

      wavesurferRef.current = wavesurfer

      // Apply cosmic glow effects to the waveform container
      if (waveformRef.current) {
        applyCosmicWaveformGlow(waveformRef.current, false)
      }

      // Set up waveform event listeners
      wavesurfer.on('ready', () => {
        setState(prev => ({ 
          ...prev, 
          duration: wavesurfer.getDuration() 
        }))
      })

      wavesurfer.on('timeupdate', (currentTime) => {
        setState(prev => ({ ...prev, currentTime }))
        onTimeUpdate?.(currentTime, wavesurfer.getDuration())
      })

      wavesurfer.on('seeking', (currentTime) => {
        setState(prev => ({ ...prev, currentTime }))
      })

      wavesurfer.on('play', () => {
        setState(prev => ({ ...prev, isPlaying: true }))
        onPlayStateChange?.(true)
        // Update glow effect for playing state
        if (waveformRef.current) {
          applyCosmicWaveformGlow(waveformRef.current, true)
        }
      })

      wavesurfer.on('pause', () => {
        setState(prev => ({ ...prev, isPlaying: false }))
        onPlayStateChange?.(false)
        // Update glow effect for paused state
        if (waveformRef.current) {
          applyCosmicWaveformGlow(waveformRef.current, false)
        }
      })

      wavesurfer.on('error', (error) => {
        // Ignore AbortError which happens when component unmounts during loading
        if (error.name === 'AbortError' || error.message?.includes('aborted')) {
          console.log('WaveSurfer load was aborted (likely due to component unmount)')
          return
        }
        
        // Handle Failed to fetch error specifically
        if (error.message?.includes('Failed to fetch')) {
          console.log('WaveSurfer: Failed to fetch audio file for waveform (continuing without waveform)')
          return // Don't show error to user, just skip waveform
        }
        
        console.error('WaveSurfer error:', error)
        handleError({
          code: 'WAVEFORM_ERROR',
          message: 'Waveform initialization error',
          details: error
        })
      })

      // Set initial volume
      wavesurfer.setVolume(finalConfig.volume || 0.7)
    }

    initializePlayer()

    // Cleanup on unmount or file change
    return () => {
      isCancelled = true
      cleanup()
    }
  }, [file, finalConfig.showWaveform, finalConfig.showVideoControls])

  // Player control methods
  const playerRef: MediaPlayerRef = {
    play: () => {
      if (videojsRef.current) {
        videojsRef.current.play()
      } else if (audioRef.current) {
        audioRef.current.play()
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.play()
      }
    },

    pause: () => {
      if (videojsRef.current) {
        videojsRef.current.pause()
      } else if (audioRef.current) {
        audioRef.current.pause()
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.pause()
      }
    },

    stop: () => {
      if (videojsRef.current) {
        videojsRef.current.pause()
        videojsRef.current.currentTime(0)
      } else if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.pause()
        wavesurferRef.current.setTime(0)
      }
    },

    seek: (time: number) => {
      if (videojsRef.current) {
        videojsRef.current.currentTime(time)
      } else if (audioRef.current) {
        audioRef.current.currentTime = time
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.setTime(time)
      }
    },

    setVolume: (volume: number) => {
      const clampedVolume = Math.max(0, Math.min(1, volume))
      
      if (videojsRef.current) {
        videojsRef.current.volume(clampedVolume)
      } else if (audioRef.current) {
        audioRef.current.volume = clampedVolume
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.setVolume(clampedVolume)
      }
    },

    getCurrentTime: () => {
      return state.currentTime
    },

    getDuration: () => {
      return state.duration
    },

    isPlaying: () => {
      return state.isPlaying
    },

    destroy: cleanup
  }

  return {
    state,
    playerRef,
    videoRef,
    waveformRef,
    audioRef
  }
}